import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInView } from 'framer-motion'
import {
  Clock,
  Award,
  Globe,
  MapPin,
  FileText,
  Factory,
  Flame,
  Users,
  Check
} from 'lucide-react'

// Hook per animazione quando entra in viewport
function useFadeIn() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-50px" })
  return { ref, isInView }
}

const statIcons = [Clock, Globe, Factory, Award]
const statKeys = ['years', 'export', 'days', 'certs']

export function CompanyPage() {
  const { t } = useTranslation('common')
  const [activeYear, setActiveYear] = useState<string | null>(null)

  const timeline = t('company.timeline.items', { returnObjects: true }) as { year: string; title: string; desc: string }[]
  const timelineSides = ['left', 'right', 'left', 'right', 'left', 'right']

  const certifications = t('company.certifications.items', { returnObjects: true }) as { name: string; desc: string }[]
  const documents = [
    { file: 'politica-integrata.pdf' },
    { file: 'report-sostenibilita.pdf' },
    { file: 'codice-etico.pdf' },
    { file: 'modello-231.pdf' },
  ]
  const docItems = t('company.documents.items', { returnObjects: true }) as { title: string; desc: string }[]

  const yeastFeatures = t('company.yeast.features', { returnObjects: true }) as string[]

  const fadeInClass = (isInView: boolean) =>
    `transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`

  const { ref: refStats, isInView: statsInView } = useFadeIn()
  const { ref: refHistory, isInView: historyInView } = useFadeIn()
  const { ref: refYeast, isInView: yeastInView } = useFadeIn()
  const { ref: refValues, isInView: valuesInView } = useFadeIn()
  const { ref: refCerts, isInView: certsInView } = useFadeIn()
  const { ref: refDocs, isInView: docsInView } = useFadeIn()

  return (
    <div className="bg-white">
      {/* STATS BAR */}
      <section ref={refStats} className="bg-neutral-900 text-white py-10 border-b-4 border-brand">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-8 ${fadeInClass(statsInView)}`}>
            {statKeys.map((key, i) => {
              const Icon = statIcons[i]
              return (
                <div key={key} className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Icon className="w-5 h-5 text-brand" />
                    <span className="text-4xl font-bold">{t(`company.stats.${key}.number`)}</span>
                  </div>
                  <p className="text-sm text-neutral-400">{t(`company.stats.${key}.label`)}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-brand font-semibold uppercase tracking-wider text-sm">{t('company.intro.label')}</span>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-neutral-900 mt-4 mb-6">
            {t('company.intro.title')}
          </h2>
          <p
            className="text-lg text-neutral-600 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: t('company.intro.text') }}
          />
        </div>
      </section>

      {/* LA NOSTRA STORIA - Timeline */}
      <section ref={refHistory} className="py-16 bg-neutral-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`text-center mb-16 ${fadeInClass(historyInView)}`}>
            <span className="text-brand font-semibold uppercase tracking-wider text-sm">{t('company.timeline.label')}</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900 mt-3">
              {t('company.timeline.title')}
            </h2>
          </div>

          {/* Desktop Timeline */}
          <div className="hidden md:block relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-neutral-300 -translate-x-1/2" />
            <div className="space-y-12">
              {timeline.map((item, idx) => {
                const isLeft = timelineSides[idx] === 'left'
                return (
                  <div
                    key={idx}
                    className={`relative grid grid-cols-[1fr_auto_1fr] gap-8 items-center ${fadeInClass(historyInView)}`}
                    style={{ transitionDelay: `${idx * 100}ms` }}
                  >
                    <div className={isLeft ? 'flex justify-end' : ''}>
                      {isLeft && (
                        <div
                          className="bg-white rounded-xl p-6 shadow-sm border border-neutral-100 cursor-pointer hover:shadow-md transition-shadow max-w-md text-left"
                          onClick={() => setActiveYear(activeYear === item.year ? null : item.year)}
                        >
                          <span className="text-brand text-sm font-semibold">{item.year}</span>
                          <h3 className="text-xl font-bold text-neutral-900 mt-1 mb-2">{item.title}</h3>
                          <p className="text-neutral-600 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                      )}
                    </div>
                    <div className="w-4 h-4 rounded-full border-4 bg-white border-neutral-300 flex-shrink-0 relative z-10" />
                    <div>
                      {!isLeft && (
                        <div
                          className="bg-white rounded-xl p-6 shadow-sm border border-neutral-100 cursor-pointer hover:shadow-md transition-shadow max-w-md"
                          onClick={() => setActiveYear(activeYear === item.year ? null : item.year)}
                        >
                          <span className="text-brand text-sm font-semibold">{item.year}</span>
                          <h3 className="text-xl font-bold text-neutral-900 mt-1 mb-2">{item.title}</h3>
                          <p className="text-neutral-600 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Mobile Timeline */}
          <div className="md:hidden space-y-6">
            {timeline.map((item, idx) => (
              <div
                key={idx}
                className={`relative pl-8 ${fadeInClass(historyInView)}`}
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className="absolute left-[7px] top-8 bottom-[-24px] w-0.5 bg-neutral-300" />
                <div className="absolute left-0 top-2 w-4 h-4 rounded-full border-4 bg-white border-neutral-300" />
                <div
                  className="bg-white rounded-xl p-5 shadow-sm border border-neutral-100 cursor-pointer"
                  onClick={() => setActiveYear(activeYear === item.year ? null : item.year)}
                >
                  <span className="text-brand text-sm font-semibold">{item.year}</span>
                  <h3 className="text-lg font-bold text-neutral-900 mt-1 mb-1">{item.title}</h3>
                  <p className="text-neutral-600 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IL LIEVITO MADRE */}
      <section ref={refYeast} className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`grid md:grid-cols-2 gap-12 items-center ${fadeInClass(yeastInView)}`}>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Flame className="w-6 h-6 text-brand" />
                <span className="text-brand font-semibold uppercase tracking-wider text-sm">{t('company.yeast.label')}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900 mb-6">
                {t('company.yeast.title')} <span className="text-brand">{t('company.yeast.titleHighlight')}</span>
              </h2>
              <p
                className="text-neutral-600 mb-6 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: t('company.yeast.text') }}
              />

              <div className="grid grid-cols-2 gap-4">
                {yeastFeatures.map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-neutral-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative mb-6 md:mb-0">
              <div className="aspect-[4/3] bg-neutral-100 rounded-2xl overflow-hidden">
                <img
                  src="/media/immagini-processo/Lievito_Madre.jpg"
                  alt={t('company.yeast.imageAlt')}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/images/company/stabilimento.jpg'
                  }}
                />
              </div>
              <div className="absolute -bottom-4 right-4 md:-right-4 bg-brand text-neutral-900 px-6 py-3 rounded-lg font-bold shadow-lg">
                {t('company.yeast.badge')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESSO - 3 Giorni */}
      <section className="py-16 bg-neutral-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-brand font-semibold uppercase tracking-wider text-sm">{t('company.process.label')}</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mt-3 mb-4">
              {t('company.process.title')}
            </h2>
            <p className="text-neutral-400 max-w-2xl mx-auto">
              {t('company.process.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {['day1', 'day2', 'day3'].map((dayKey, i) => (
              <div key={dayKey} className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-brand/50 transition-colors">
                <div className="w-12 h-12 bg-brand text-neutral-900 rounded-full flex items-center justify-center font-bold text-xl mb-4">
                  {i + 1}
                </div>
                <h3 className="text-xl font-bold mb-2 text-white">
                  {t('company.process.dayLabel')} {i + 1}: {t(`company.process.${dayKey}.title`)}
                </h3>
                <p className="text-neutral-400 text-sm mb-4">{t(`company.process.${dayKey}.desc`)}</p>
                <div className="flex items-center gap-2 text-brand text-sm">
                  <Clock className="w-4 h-4" />
                  {t(`company.process.${dayKey}.time`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALORI */}
      <section ref={refValues} className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`text-center mb-12 ${fadeInClass(valuesInView)}`}>
            <span className="text-brand font-semibold uppercase tracking-wider text-sm">{t('company.values.label')}</span>
            <h2 className="text-3xl font-heading font-bold text-neutral-900 mt-3">{t('company.values.title')}</h2>
          </div>

          <div className={`grid md:grid-cols-2 gap-8 ${fadeInClass(valuesInView)}`}>
            {statKeys.map((key, i) => {
              const Icon = statIcons[i]
              return (
                <div key={key} className="flex gap-4 p-6 bg-neutral-50 rounded-xl">
                  <div className="w-12 h-12 bg-brand/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-brand" />
                  </div>
                  <div>
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-2xl font-bold text-brand">{t(`company.stats.${key}.number`)}</span>
                      <span className="text-neutral-500">{t(`company.stats.${key}.label`)}</span>
                    </div>
                    <p className="text-neutral-600 text-sm">{t(`company.stats.${key}.text`)}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* CERTIFICAZIONI */}
      <section ref={refCerts} className="py-16 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`text-center mb-10 ${fadeInClass(certsInView)}`}>
            <span className="text-brand font-semibold uppercase tracking-wider text-sm">{t('company.certifications.label')}</span>
            <h2 className="text-3xl font-heading font-bold text-neutral-900 mt-3">{t('company.certifications.title')}</h2>
          </div>

          <div className={`flex flex-wrap justify-center gap-4 ${fadeInClass(certsInView)}`}>
            {certifications.map((cert, i) => (
              <div key={i} className="bg-white px-6 py-4 rounded-lg shadow-sm border border-neutral-100 text-center min-w-[160px]">
                <Award className="w-6 h-6 text-brand mx-auto mb-2" />
                <div className="font-semibold text-neutral-900">{cert.name}</div>
                <div className="text-xs text-neutral-500">{cert.desc}</div>
              </div>
            ))}
            <div className="bg-brand px-6 py-4 rounded-lg text-center min-w-[160px]">
              <Award className="w-6 h-6 text-neutral-900 mx-auto mb-2" />
              <div className="font-semibold text-neutral-900">{t('company.certifications.greatTaste')}</div>
              <div className="text-xs text-neutral-800">{t('company.certifications.greatTasteDesc')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* TERRITORIO & GRUPPO */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <MapPin className="w-6 h-6 text-brand mb-4" />
              <h3 className="text-2xl font-bold text-neutral-900 mb-4">{t('company.territory.title')}</h3>
              <p className="text-neutral-600 mb-6">
                {t('company.territory.text')}
              </p>
              <div className="flex gap-4 text-sm">
                <div className="bg-neutral-100 px-4 py-2 rounded">
                  <strong className="text-brand">40km</strong> <span className="text-neutral-600">{t('company.territory.fromTurin')}</span>
                </div>
                <div className="bg-neutral-100 px-4 py-2 rounded">
                  <strong className="text-brand">80km</strong> <span className="text-neutral-600">{t('company.territory.fromLanghe')}</span>
                </div>
              </div>
            </div>

            <div>
              <Users className="w-6 h-6 text-brand mb-4" />
              <h3 className="text-2xl font-bold text-neutral-900 mb-4">{t('company.group.title')}</h3>
              <p className="text-neutral-600 mb-6">
                {t('company.group.text')}
              </p>
              <div className="flex items-center gap-6">
                <div>
                  <div className="text-3xl font-bold text-brand">1860</div>
                  <div className="text-sm text-neutral-500">{t('company.group.founded')}</div>
                </div>
                <div className="h-10 w-px bg-neutral-200" />
                <div>
                  <div className="text-3xl font-bold text-brand">160+</div>
                  <div className="text-sm text-neutral-500">{t('company.group.yearsHistory')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DOCUMENTI */}
      <section ref={refDocs} className="py-16 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`text-center mb-10 ${fadeInClass(docsInView)}`}>
            <span className="text-brand font-semibold uppercase tracking-wider text-sm">{t('company.documents.label')}</span>
            <h2 className="text-3xl font-heading font-bold text-neutral-900 mt-3">{t('company.documents.title')}</h2>
          </div>

          <div className={`grid md:grid-cols-4 gap-4 ${fadeInClass(docsInView)}`}>
            {docItems.map((doc, i) => (
              <a
                key={i}
                href={`/documents/${documents[i]?.file || ''}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white p-5 rounded-lg shadow-sm border border-neutral-100 hover:border-brand/30 hover:shadow-md transition-all group"
              >
                <FileText className="w-8 h-8 text-brand mb-3 group-hover:scale-110 transition-transform" />
                <h4 className="font-semibold text-neutral-900">{doc.title}</h4>
                <p className="text-sm text-neutral-500">{doc.desc}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

    </div>
  )
}
